import type { Company, PersonCompanyRelationship } from '@/types/person/companies';
import { COMPANY_TYPE_LABELS, CompanyTypeEnum, TrustStatus } from '@/types/person/companies';
import { companiesService } from '~/services/companies-service';
import { publicService } from '~/services/public-service';
import type { BaseAPIResponse } from '~/types/api';

export const useCompaniesStore = defineStore(
    'companies',
    () => {
        const personStore = usePersonStore();
        const { showError } = useGlobalToast();
        const selectedCompany = ref<Company | null>(null);
        const companies = ref<Company[]>([]);
        const companyCacheTimestamps = ref<Record<number, number>>({});
        const route = useRoute();
        const showIncident = ref(false);
        const deletionGuide = ref<string>();
        const companyResponse = ref<BaseAPIResponse<Company[]>>();
        const isLoading = ref(false);
        const categories = ref<Record<string, string>>({});

        // External search state
        const externalSearchResults = ref<Company[]>([]);
        const isSearchingExternal = ref(false);
        const hasMoreExternalResults = ref(true);
        const externalSearchPage = ref(1);
        const isInExternalSearchDebounce = ref(false);
        const companiesSidebarScrollTop = ref(0);

        // Tab data caching system
        const tabDataCache = ref<
            Map<
                number,
                {
                    channels: { data: any[]; loaded: boolean; timestamp: number };
                    conversation: { data: any[]; loaded: boolean; timestamp: number };
                    deletionGuide: { data: string | null; loaded: boolean; timestamp: number };
                    advisories: { data: any[]; loaded: boolean; timestamp: number };
                    faqs: { data: any[]; loaded: boolean; timestamp: number };
                }
            >
        >(new Map());

        // Risk factors caching system
        const riskFactorsCache = ref<Map<number, { data: any; loaded: boolean; timestamp: number }>>(new Map());

        // Cache expiration time (5 minutes)
        const CACHE_EXPIRY = 5 * 60 * 1000;

        // Helper function to get or create cache entry for a company
        const getCacheEntry = (companyId: number) => {
            if (!tabDataCache.value.has(companyId)) {
                tabDataCache.value.set(companyId, {
                    channels: { data: [], loaded: false, timestamp: 0 },
                    conversation: { data: [], loaded: false, timestamp: 0 },
                    deletionGuide: { data: null, loaded: false, timestamp: 0 },
                    advisories: { data: [], loaded: false, timestamp: 0 },
                    faqs: { data: [], loaded: false, timestamp: 0 },
                });
            }
            return tabDataCache.value.get(companyId)!;
        };

        // Helper function to check if cache is valid
        const isCacheValid = (timestamp: number) => {
            return Date.now() - timestamp < CACHE_EXPIRY;
        };

        // Helper function to add company to cache with timestamp
        const addCompanyToCache = (company: Company) => {
            const expiryTime = Date.now() + CACHE_EXPIRY;
            companyCacheTimestamps.value[company.id] = expiryTime;

            // Add company to companies array if not already present
            if (!companies.value.some((c) => c.id === company.id)) {
                companies.value.push(company);
            }
        };

        // Clear cache when company changes
        const clearCompanyCache = (companyId: number) => {
            tabDataCache.value.delete(companyId);
        };

        // Watch for company changes and clear old cache
        watch(selectedCompany, (newCompany, oldCompany) => {
            if (oldCompany && newCompany && oldCompany.id !== newCompany.id) {
                clearCompanyCache(oldCompany.id);
            }
        });

        const relationships = ref<PersonCompanyRelationship[]>([]);
        const searchQuery = ref('');
        const relationshipsPage = ref(1);
        const hasMoreRelationships = ref(true);
        const isLoadingRelationships = ref(false);

        const relationship = computed(() => {
            return relationships.value.find((r) => r.company_id === selectedCompany.value?.id);
        });

        // Computed property to get company by ID, fetching if not exists
        const getCompanyById = computed(() => {
            return (companyId: number) => {
                // Check if company exists and cache is valid
                const company = companies.value.find((c: Company) => c.id === companyId);

                            if (company) {
                const timestamp = companyCacheTimestamps.value[companyId];
                if (!timestamp || !isCacheValid(timestamp)) {
                    fetchCompany(companyId).catch((error) => {
                        console.error(`Failed to fetch company ${companyId}:`, error);
                    });
                } 

                return company;
            }

                // If company not found, fetch it asynchronously
                fetchCompany(companyId).catch((error) => {
                    console.error(`Failed to fetch company ${companyId}:`, error);
                });

                // Return undefined initially, will update when fetch completes
                return undefined;
            };
        });

        // Computed property for filtered companies based on search query
        const filteredCompanies = computed(() => {
            if (!searchQuery.value) {
                return companies.value;
            } else {
                return companies.value.filter((company) => company.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
            }
        });

        // Getter for category keys used in searching
        const categoryKeys = computed(() => {
            return Object.values(CompanyTypeEnum);
        });

        const selectedCategoryType = ref<string[]>([]);
        const totalDataBrokers = ref(0);

            function resetCompanies() {
        companies.value = [];
        companyCacheTimestamps.value = {};
        companyResponse.value = undefined;
    }

        async function fetchTrustCompany(slug: string) {
            try {
                const data = await publicService.getCompanyBySlug(slug);
                selectedCompany.value = data;
                // Add company to cache with timestamp
                addCompanyToCache(data);
            } catch (error: any) {
                showError('Error', error.message || 'Failed to fetch company');
            }
        }

        async function fetchPersonCompanyRelationships(page: number = 1, per_page: number = 20) {
            if (!personStore.person) return;

            try {
                isLoadingRelationships.value = true;
                const response = await companiesService.getPersonCompanyRelationships(personStore.person.id, page, per_page);

                if (page === 1) {
                    relationships.value = response.data;
                } else {
                    relationships.value.push(...response.data);
                }

                // Update pagination state
                hasMoreRelationships.value =
                    response.meta?.current_page && response.meta?.last_page
                        ? response.meta.current_page < response.meta.last_page
                        : false;

                if (hasMoreRelationships.value) {
                    relationshipsPage.value = page + 1;
                }

                // Fetch company details for each relationship
                await loadCompaniesFromRelationships();

                // Select company based on URL parameter if available
                const companyName = route.query.company;
                if (companyName) {
                    const company = companies.value.find((c: Company) => c.name === companyName);
                    if (company) {
                        selectedCompany.value = company;
                    }
                }
            } catch (error: any) {
                showError('Error', error.message || 'Failed to fetch companies');
                throw error;
            } finally {
                isLoadingRelationships.value = false;
            }
        }

        async function loadMoreRelationships() {
            if (isLoadingRelationships.value || !hasMoreRelationships.value) return;
            await fetchPersonCompanyRelationships(relationshipsPage.value);
        }

        async function loadCompaniesFromRelationships() {
            if (!relationships.value.length) return;

            // Filter out relationships for companies that are already loaded
            const unloadedRelationships = relationships.value.filter(
                (rel) => !companies.value.some((c) => c.id === rel.company_id)
            );

            if (unloadedRelationships.length === 0) return;

            const batchSize = 5;

            // Process unloaded relationships in batches
            for (let i = 0; i < unloadedRelationships.length; i += batchSize) {
                const batch = unloadedRelationships.slice(i, i + batchSize);

                // Create promises for all companies in this batch
                const promises = batch.map(async (rel) => {
                    try {
                        const company = await companiesService.getCompany(rel.company_id);

                        // Add company to cache with timestamp
                        addCompanyToCache(company);

                        return { success: true, companyId: rel.company_id };
                    } catch (error: any) {
                        console.error(`Failed to load company ${rel.company_id}:`, error);

                        // Remove failed relationship
                        const relationshipIndex = relationships.value.findIndex((r) => r.company_id === rel.company_id);
                        if (relationshipIndex !== -1) {
                            relationships.value.splice(relationshipIndex, 1);
                        }

                        return { success: false, companyId: rel.company_id };
                    }
                });

                // Wait for all promises in this batch to complete
                await Promise.all(promises);
            }
        }

        async function fetchPublicCompanies(
            query: string = '',
            per_page: number = 50,
            page: number = 1,
            type: Array<string> = []
        ) {
            if (query && query.length < 3) return;

                    isLoading.value = true;
        searchQuery.value = query;
        selectedCategoryType.value = type;

            try {
                const data = await publicService.searchCompaniesV3(query, per_page, page, type.filter(t => t.trim()).join(','));
                if (page === 1) {
                    // Clear existing companies and add new ones with timestamps
                    companies.value = [];
                    data.data.forEach((company) => addCompanyToCache(company));

                    // Only select company based on URL parameter on first page load
                    const companyName = route.query.company;
                    if (companyName) {
                        const company = companies.value.find((c: Company) => c.name === companyName);
                        if (company) {
                            selectedCompany.value = company;
                        }
                    }
                } else {
                    // Add new companies with timestamps
                    data.data.forEach((company) => addCompanyToCache(company));
                }
                companyResponse.value = data;
            } catch (error: any) {
                showError('Error', error.message || 'Failed to fetch public companies');
            } finally {
                isLoading.value = false;
            }
        }

        async function fetchDataBrokerCompanies(
            query: string = '',
            per_page: number = 50,
            page: number = 1,
            trust_page: boolean = false
        ) {
            if (query && query.length < 3) return;

            try {
                const data = await publicService.getDataBrokerCompanies(query, per_page, page, trust_page);

                // Select company based on URL parameter if available
                const companyName = route.query.company;
                if (companyName) {
                    const company = companies.value.find((c: Company) => c.name === companyName);
                    if (company) {
                        selectedCompany.value = company;
                    }
                }
            } catch (error: any) {
                showError('Error', error.message || 'Failed to fetch data broker companies');
                throw error;
            }
        }

        async function fetchCompany(id: number) {
            isLoading.value = true;

            try {
                const company = await companiesService.getCompany(id);
                selectedCompany.value = company;

                // Add company to cache with timestamp
                addCompanyToCache(company);
            } catch (error: any) {
                showError(
                    'Error',
                    error.message || `Something went wrong while fetching company. Code: GECOMP${error.code || 'UNKNOWN'}`
                );
            } finally {
                isLoading.value = false;
            }
        }

        async function selectCompany(id: number) {
            try {
                // First try to find the company in the store with valid cache
                let company = companies.value.find((c: Company) => c.id === id);
                if (company) {
                    selectedCompany.value = company;

                    const timestamp = companyCacheTimestamps.value[id];
                    if (!timestamp || isCacheValid(timestamp)) {
                        selectedCompany.value = company;
                    }
                } else {
                    // If company not in store, try to fetch it
                    await fetchCompany(id);
                    company = companies.value.find((c: Company) => c.id === id);

                    if (company) {
                        selectedCompany.value = company;
                    } else {
                        throw new Error(`Company with ID ${id} not found in store and could not be fetched`);
                    }
                }
            } catch (error) {
                throw error;
            }
        }

        async function selectCompanyBySlug(slug: string) {
            try {
                // First try to find the company in the store by slug
                let company = companies.value.find((c: Company) => c.slug === slug);
                if (company) {
                    selectedCompany.value = company;
                    return;
                }

                // If company not in store, fetch it by slug
                const fetchedCompany = await companiesService.getCompanyBySlug(slug);

                // Add company to cache with timestamp
                addCompanyToCache(fetchedCompany);

                // Set as selected company
                selectedCompany.value = fetchedCompany;
            } catch (error) {
                throw error;
            }
        }

        const requestDeletionGuide = async (companyId: number) => {
            try {
                deletionGuide.value = await companiesService.getDeletionGuide(companyId);
            } catch (error: any) {
                showError('Error', error.message || 'Failed to fetch deletion guide');
                throw error;
            }
        };

        // Cached data fetching methods for tabs
        const getCompanyChannels = async (companyId: number, personId: number, forceRefresh = false) => {
            const cache = getCacheEntry(companyId);

            if (!forceRefresh && cache.channels.loaded && isCacheValid(cache.channels.timestamp)) {
                return cache.channels.data;
            }

            try {
                const data = await companiesService.getCompanyChannels(personId, companyId);
                cache.channels.data = data;
                cache.channels.loaded = true;
                cache.channels.timestamp = Date.now();
                return data;
            } catch (error) {
                console.error('Failed to fetch company channels:', error);
                return [];
            }
        };

        const getCompanyConversation = async (companyId: number, personId: number, forceRefresh = false) => {
            const cache = getCacheEntry(companyId);

            if (!forceRefresh && cache.conversation.loaded && isCacheValid(cache.conversation.timestamp)) {
                return cache.conversation.data;
            }

            try {
                const data = await companiesService.getCompanyConversation(personId, companyId);
                cache.conversation.data = data;
                cache.conversation.loaded = true;
                cache.conversation.timestamp = Date.now();
                return data;
            } catch (error) {
                console.error('Failed to fetch company conversation:', error);
                return [];
            }
        };

        const getCompanyDeletionGuide = async (companyId: number, forceRefresh = false) => {
            const cache = getCacheEntry(companyId);

            if (!forceRefresh && cache.deletionGuide.loaded && isCacheValid(cache.deletionGuide.timestamp)) {
                return cache.deletionGuide.data;
            }

            try {
                const data = await companiesService.getDeletionGuide(companyId);
                cache.deletionGuide.data = data;
                cache.deletionGuide.loaded = true;
                cache.deletionGuide.timestamp = Date.now();
                return data;
            } catch (error) {
                console.error('Failed to fetch company deletion guide:', error);
                return null;
            }
        };

        const getCompanyAdvisories = async (companyId: number, forceRefresh = false) => {
            const cache = getCacheEntry(companyId);

            if (!forceRefresh && cache.advisories.loaded && isCacheValid(cache.advisories.timestamp)) {
                return cache.advisories.data;
            }

            try {
                const data = await companiesService.getCompanyAdvisories(companyId);
                cache.advisories.data = data;
                cache.advisories.loaded = true;
                cache.advisories.timestamp = Date.now();
                return data;
            } catch (error) {
                console.error('Failed to fetch company advisories:', error);
                return [];
            }
        };

        const getCompanyFaqs = async (companyId: number, forceRefresh = false) => {
            const cache = getCacheEntry(companyId);

            if (!forceRefresh && cache.faqs.loaded && isCacheValid(cache.faqs.timestamp)) {
                return cache.faqs.data;
            }

            try {
                const data = await companiesService.getCompanyFaqs(companyId);
                cache.faqs.data = data;
                cache.faqs.loaded = true;
                cache.faqs.timestamp = Date.now();
                return data;
            } catch (error) {
                console.error('Failed to fetch company FAQs:', error);
                return [];
            }
        };

        // Risk factors caching and fetching
        async function getRiskFactors(companyId: number) {
            if (!companyId) return null;

            const cached = riskFactorsCache.value.get(companyId);
            const now = Date.now();

            // Return cached data if it exists and isn't expired
            if (cached && cached.loaded && (now - cached.timestamp < CACHE_EXPIRY)) {
                return cached.data;
            }

            try {
                // Mark as loading
                riskFactorsCache.value.set(companyId, {
                    data: cached?.data || null,
                    loaded: false,
                    timestamp: now
                });

                const data = await companiesService.getCompanyRiskFactors(companyId);
                
                // Cache the result
                riskFactorsCache.value.set(companyId, {
                    data,
                    loaded: true,
                    timestamp: now
                });

                return data;
            } catch (error) {
                console.error('Failed to fetch risk factors:', error);
                
                // Cache the failure
                riskFactorsCache.value.set(companyId, {
                    data: null,
                    loaded: true,
                    timestamp: now
                });
                
                return null;
            }
        };

        const isRiskFactorsLoading = (companyId: number): boolean => {
            const cached = riskFactorsCache.value.get(companyId);
            return cached ? !cached.loaded : false;
        };

        // External search functions
        async function searchExternalCompanies(query: string, append = false) {
            if (isSearchingExternal.value || !hasMoreExternalResults.value) return;

            isSearchingExternal.value = true;

            try {
                const response = await companiesService.searchCompanies(
                    query,
                    20, // per_page
                    externalSearchPage.value,
                    selectedCategoryType.value.length > 0 ? selectedCategoryType.value.join(',') : ''
                );

                // For external search, don't filter out duplicates - show all results
                // This allows users to see and select companies even if they exist in relationships
                const filteredExternalResults = response.data;

                if (append) {
                    externalSearchResults.value.push(...filteredExternalResults);
                } else {
                    externalSearchResults.value = filteredExternalResults;
                }

                // Check if there are more results
                hasMoreExternalResults.value = response.meta?.current_page && response.meta?.last_page
                    ? response.meta.current_page < response.meta.last_page
                    : false;

                if (hasMoreExternalResults.value) {
                    externalSearchPage.value++;
                }
            } catch (error) {
                console.error('External search failed:', error);
            } finally {
                isSearchingExternal.value = false;
            }
        }

        function clearExternalSearchResults() {
            externalSearchResults.value = [];
            externalSearchPage.value = 1;
            hasMoreExternalResults.value = true;
            isSearchingExternal.value = false;
        }

        function setExternalSearchDebounce(isDebouncing: boolean) {
            isInExternalSearchDebounce.value = isDebouncing;
        }

        function addTemporaryRelationship(companyId: number) {
            // Check if relationship already exists
            const existingRelationship = relationships.value.find(rel => rel.company_id === companyId);
            if (!existingRelationship) {
                // Add a temporary relationship
                relationships.value.unshift({
                    id: Date.now(), // Temporary ID
                    company_id: companyId,
                    person_id: 0, // Will be populated when/if the relationship becomes permanent
                    relation: TrustStatus.UNTRUSTED,
                    is_temporary: true // Flag to indicate this is temporary
                });
            }
        }

        async function fetchCategoryStats() {
            isLoading.value = true;

            try {
                const data = await publicService.getCompanyStats();

                const brokerTypes: Record<string, number> = data.data_broker_types;

                if (!brokerTypes) {
                    console.warn('No data_broker_types found in response');
                    return;
                }

                totalDataBrokers.value = data.data_brokers || 0;

                categories.value = Object.keys(brokerTypes).reduce((acc, key) => {
                    if (COMPANY_TYPE_LABELS[key as CompanyTypeEnum]) {
                        const label = COMPANY_TYPE_LABELS[key as CompanyTypeEnum];
                        const count = brokerTypes[key];
                        acc[key] = `${label} (${count})`;
                    }
                    return acc;
                }, {} as Record<string, string>);
            } catch (error: any) {
                showError('Error', error.message || 'Failed to fetch category stats');
            } finally {
                isLoading.value = false;
            }
        }

        return {
            selectedCompany,
            companies,
            filteredCompanies,
            relationships,
            searchQuery,
            relationshipsPage,
            hasMoreRelationships,
            isLoadingRelationships,
            fetchPersonCompanyRelationships,
            loadMoreRelationships,
            loadCompaniesFromRelationships,
            selectCompany,
            selectCompanyBySlug,
            showIncident,
            deletionGuide,
            fetchPublicCompanies,
            fetchDataBrokerCompanies,
            companyResponse,
            isLoading,
            resetCompanies,
            categories,
            categoryKeys,
            fetchCategoryStats,
            selectedCategoryType,
            totalDataBrokers,
            fetchCompany,
            fetchTrustCompany,
            requestDeletionGuide,
            relationship,
            getCompanyById,
            getCompanyChannels,
            getCompanyConversation,
            getCompanyDeletionGuide,
            getCompanyAdvisories,
            getCompanyFaqs,
            getRiskFactors,
            isRiskFactorsLoading,
            externalSearchResults,
            isSearchingExternal,
            hasMoreExternalResults,
            isInExternalSearchDebounce,
            searchExternalCompanies,
            clearExternalSearchResults,
            setExternalSearchDebounce,
            addTemporaryRelationship,
            clearCompanyCache,
            addCompanyToCache,
            companyCacheTimestamps,
            companiesSidebarScrollTop
        };
    },
    {
        persist: {
            pick: ['companies', 'companyCacheTimestamps'],
        },
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCompaniesStore, import.meta.hot));
}
